"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TrackChanges = void 0;
const delta_1 = require("./delta");
const lodash_1 = require("lodash");
class TrackChanges {
    constructor(initial, deltas = []) {
        this.initial = initial;
        // mutate the initial object while tracking changes
        this.final = initial;
        this.history = {
            undo: [...deltas],
            redo: [],
            uncommitted: [],
            committed: [[...deltas]] // assume initialized deltas have already been committed
        };
    }
    copy() {
        return Object.assign({}, this.final);
    }
    /**
     * Check if path is an array reference with index value and return index;
     * otherwise, return -1 if not reference to an array index value.
     * @param path - JSON path dot notation
     */
    parsePathArray(path) {
        let index = -1;
        if (path[path.length - 1] === ']') {
            index = +path.substring(path.lastIndexOf('[') + 1, path.length - 1);
            path = path.substring(0, path.lastIndexOf('['));
        }
        return { index, path };
    }
    copyValue(value) {
        if (Array.isArray(value)) {
            return [...value];
        }
        else if (value !== null && typeof value === 'object') {
            return Object.assign({}, value);
        }
        else {
            return value;
        }
    }
    apply(delta) {
        switch (delta.type) {
            case delta_1.DeltaType.Insert:
                return this.insert(delta.path, delta.newValue);
            case delta_1.DeltaType.Move:
                return this.move(delta.path, delta.oldValue, delta.newValue);
            case delta_1.DeltaType.Delete:
                return this.delete(delta.path);
            case delta_1.DeltaType.Update:
                return this.update(delta.path, delta.newValue);
        }
    }
    get(path) {
        return lodash_1.get(this.final, path);
    }
    insert(path, newValue) {
        const parsedPath = this.parsePathArray(path);
        const array = lodash_1.get(this.final, parsedPath.path);
        if (!Array.isArray(array)) {
            throw new Error(`\`${path}\` is not an array.`);
        }
        if (parsedPath.index === -1) {
            array.push(newValue);
        }
        else {
            array.splice(parsedPath.index, 0, newValue);
        }
        const delta = {
            type: delta_1.DeltaType.Insert,
            path,
            // copy value to prevent outside mutation
            newValue: this.copyValue(newValue)
        };
        this.addUndo(delta);
        return delta;
    }
    move(path, sourceIndex, destinationIndex) {
        const array = lodash_1.get(this.final, path);
        if (!Array.isArray(array)) {
            throw new Error(`\`${path}\` is not an array.`);
        }
        const [item] = array.splice(sourceIndex, 1);
        // if moving forward then account for the source being removed
        // const offset = destinationIndex >= sourceIndex ? -1 : 0;
        array.splice(destinationIndex, 0, item);
        const delta = {
            type: delta_1.DeltaType.Move,
            path,
            oldValue: sourceIndex,
            newValue: destinationIndex
        };
        this.addUndo(delta);
        return delta;
    }
    /**
     * Alias for `delete` method
     */
    remove(path) {
        return this.delete(path);
    }
    delete(path) {
        const parsedPath = this.parsePathArray(path);
        // copy the value to prevent outside mutation
        const oldValue = this.copyValue(lodash_1.get(this.final, path));
        if (parsedPath.index === -1) {
            // remove property from object
            lodash_1.unset(this.final, path);
        }
        else {
            // remove item from array
            const array = lodash_1.get(this.final, parsedPath.path);
            if (!array)
                throw new Error(`\`${path}\` is not an array.`);
            array.splice(parsedPath.index, 1);
        }
        const delta = {
            type: delta_1.DeltaType.Delete,
            path,
            oldValue
        };
        this.addUndo(delta);
        return delta;
    }
    update(path, newValue) {
        const oldValue = lodash_1.get(this.final, path);
        let delta = undefined;
        if (this.history.undo.length > 0) {
            const lastDelta = this.history.undo[this.history.undo.length - 1];
            if (lastDelta.type === delta_1.DeltaType.Update &&
                lastDelta.path === path &&
                JSON.stringify(lastDelta.newValue) === JSON.stringify(oldValue) &&
                this.history.uncommitted.includes(lastDelta)) {
                // update last delta if only updating the newValue
                // (i.e. prevent text field onChange from flooding deltas)
                lastDelta.newValue = this.copyValue(newValue);
                delta = lastDelta;
            }
        }
        if (!delta) {
            delta = {
                type: delta_1.DeltaType.Update,
                path,
                // copy the value to prevent outside mutation
                oldValue: this.copyValue(oldValue),
                newValue: this.copyValue(newValue)
            };
            this.addUndo(delta);
        }
        lodash_1.set(this.final, path, newValue);
        return delta;
    }
    addUndo(delta) {
        this.history.undo.push(delta);
        this.history.uncommitted.push(delta);
    }
    undo() {
        const delta = this.history.undo.pop();
        if (!delta)
            return;
        // look for delta in uncommitted queue and remove it
        const index = this.history.uncommitted.indexOf(delta);
        if (index > -1) {
            this.history.uncommitted.splice(index, 1);
        }
        this.history.redo.push(delta);
        switch (delta.type) {
            case delta_1.DeltaType.Insert:
                this.undoInsert(delta);
                break;
            case delta_1.DeltaType.Move:
                this.undoMove(delta);
                break;
            case delta_1.DeltaType.Delete:
                this.undoDelete(delta);
                break;
            case delta_1.DeltaType.Update:
                this.undoUpdate(delta);
                break;
        }
        // return committed status so calling app can determine if additional
        // handling is needed (i.e. database undo logic)
        return { delta, committed: index === -1 };
    }
    undoInsert(delta) {
        const parsedPath = this.parsePathArray(delta.path);
        const array = lodash_1.get(this.final, parsedPath.path);
        if (parsedPath.index === -1) {
            // remove last item from array
            array.pop();
        }
        else {
            // remove item at index
            array.splice(parsedPath.index, 1);
        }
        return delta;
    }
    undoMove(delta) {
        const array = lodash_1.get(this.final, delta.path);
        const [item] = array.splice(delta.newValue, 1);
        // if moving forward then account for the source being removed
        const offset = delta.oldValue >= delta.newValue ? -1 : 0;
        array.splice(delta.oldValue + offset, 0, item);
        return delta;
    }
    undoDelete(delta) {
        const parsedPath = this.parsePathArray(delta.path);
        const value = lodash_1.get(this.final, parsedPath.path);
        if (parsedPath.index === -1) {
            // set the old property value
            lodash_1.set(this.final, parsedPath.path, delta.oldValue);
        }
        else {
            // add item back into array
            value.splice(parsedPath.index, 0, delta.oldValue);
        }
        return delta;
    }
    undoUpdate(delta) {
        lodash_1.set(this.final, delta.path, delta.oldValue);
        return delta;
    }
    redo() {
        const delta = this.history.redo.pop();
        if (!delta)
            return;
        // TODO: how to handle uncommit
        this.apply(delta);
        return delta;
    }
    /**
     * Move all uncommitted Deltas to committed queue
     */
    commit() {
        const deltas = [...this.history.uncommitted];
        this.history.committed.push(deltas);
        this.history.uncommitted = [];
        return deltas;
    }
    /**
     * Move last batch of committed Deltas back to the uncommitted queue
     */
    uncommit() {
        const lastBatch = this.history.committed.pop();
        if (lastBatch) {
            this.history.uncommitted.unshift(...lastBatch);
        }
        return lastBatch;
    }
}
exports.TrackChanges = TrackChanges;
//# sourceMappingURL=track-changes.js.map